import { useId, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * @typedef {string | number} RadioValue
 */

/**
 * @typedef {(focused: boolean, selected: boolean) => string} ClassNameFn
 */

/**
 * @typedef {(focused: boolean, selected: boolean) => React.CSSProperties} StyleFn
 */

/**
 * @typedef {({
 *    requiredProps: {
 *       role: string;
 *       tabIndex: number;
 *       onFocus: () => void;
 *       onClick: () => void;
 *     };
 *     selected: boolean;
 *     focused: boolean;
 * }) => React.ReactNode} RenderFn
 */

/**
 * @param props
 * @param {string} [props.name]
 * @param {RadioValue} props.value
 * @param {string} props.label
 * @param {string} [props.description]
 * @param {boolean} props.selected
 * @param {(value: RadioValue) => void} [props.onSelect]
 *
 * @param {string | ClassNameFn} [props.containerClassName]
 * @param {string | ClassNameFn} [props.className]
 * @param {React.CSSProperties | StyleFn} [props.style]
 *
 * @param {RenderFn} [props.render]
 *
 * @returns {JSX.Element}
 */
export function Radio({
  name,
  value,
  label,
  description,
  selected,
  onSelect,

  containerClassName,
  className,
  style,

  render,
}) {
  const inputId = useId();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const inputRef = useRef();

  const [focused, setFocused] = useState(false);

  return (
    <div
      className={
        typeof containerClassName === 'function'
          ? containerClassName(focused, selected)
          : containerClassName
      }
    >
      <label htmlFor={inputId} className="sr-only">
        {!!description && `${description} - `}
        {label || value}
      </label>

      <input
        ref={inputRef}
        type="radio"
        id={inputId}
        name={name}
        value={value}
        checked={selected}
        className="sr-only"
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          setFocused(false);
        }}
        onChange={() => {
          onSelect(value);
        }}
      />

      {!!render &&
        render({
          requiredProps: {
            role: 'radio',
            'aria-checked': selected,
            tabIndex: -1,
            onFocus: () => inputRef.current?.focus(),
            onClick: () => onSelect(value),
          },
          selected,
          focused,
        })}

      {!render && (
        <button
          type="button"
          role="radio"
          aria-checked={selected}
          tabIndex={-1}
          className={clsx(
            'py-[8px] px-[16px]',
            'text-[16px] text-wtf-white',
            selected && 'bg-wtf-white/10',
            typeof className === 'function'
              ? className(focused, selected)
              : className,
          )}
          style={{
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: focused
              ? 'rgba(255, 255, 255, 0.6)'
              : 'rgba(255, 255, 255, 0.2)',
            ...(() => {
              if (typeof style === 'function') {
                return style(focused, selected);
              }

              return style;
            })(),
          }}
          onFocus={() => {
            inputRef.current?.focus();
          }}
          onClick={() => {
            onSelect(value);
          }}
        >
          <span>{description || label || value}</span>
        </button>
      )}
    </div>
  );
}
